// Provenance: repo:tds-reconciliation-portal:frontend/src/components/NameMappingList.tsx
// (ADR-0005) — restyled on the estate tokens (ADR-0010). Ids became UUID strings; the page
// identity moved into the workspace frame's tab bar, so `MappingHeader` renders the chip
// strip only. The three-zone geometry, the gated suggestion lists, the weld
// confirm/split flow and the rules tab carry unchanged.
//
//   Zone 1  Suggested pairs  — the few that pass the bar (strong evidence,
//                              closes money, crossed-guard). Two equal columns.
//   Zone 2  Match names      — the AliasPicker surface, composed in AliasPage.
//   Zone 3  Decided          — mapped, engine-joined audits, claimed, retired.
//   Banner  Withheld         — an exception, never an archive.
import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'

import { useCreateAliasGroups, useRejectPair } from '../api/aliases'
import { ApiError } from '../api/client'
import { useParties } from '../api/hooks'
import {
  basisLabel,
  categorySections,
  confirmJoin,
  defaultZone,
  gateText,
  headerLines,
  namesToDecide,
  namesToDecideParts,
  pairingOf,
  partySort,
  standAlone,
  strongCandidates,
  suggestionsFor,
  useAcceptPairing,
  workbenchState,
} from '../api/nameMapping'
import type { PartySortMode } from '../api/nameMapping'
import type { NameMappingPage, NameMappingRow, NameMappingZones } from '../api/types'
import { Chip, type Tone } from './badges'
import { ChipStrip, StatChip } from './ChipStrip'
import { InfoTip } from './InfoTip'
import { MatchWorkbench } from './MatchWorkbench'
import { Btn } from './Modal'
import { Money } from './Money'
import { showToast } from './toast'

const fmt = new Intl.NumberFormat('en-IN')

const th = 'px-2 py-1.5 text-left text-xs font-medium uppercase text-muted-foreground'
const td = 'px-2 py-1.5 align-top'

/** The header chip strip — the page's honest statement of how much is left and
 *  what the run looks like. The figures with the same per-unit breakdowns (as
 *  tooltips); every figure states what it counts, and the decompositions come
 *  from the unit-tested `headerLines`. */
export function MappingHeader({ summary }: { summary: NameMappingPage['summary'] }) {
  const s = summary
  const lines = headerLines(s)
  const p = namesToDecideParts(s)
  return (
    <ChipStrip>
      {lines.slice(0, 2).map((l) => (
        <span
          key={l.label}
          title={[
            ...(l.derived ? [`in ${fmt.format(l.derived.n)} ${l.derived.unit}`] : []),
            ...l.parts.map((x) => `${fmt.format(x.n)} ${x.label}`),
          ].join(' · ')}
        >
          <StatChip label={`${l.label} ${l.unit}`} value={l.total} />
        </span>
      ))}
      <span
        title={[
          `${fmt.format(p.only_26as)} in 26AS only`,
          `${fmt.format(p.only_books)} in books only`,
          ...(p.claimed > 0
            ? [`less ${fmt.format(p.claimed)} already mapped, resolving next run`]
            : []),
        ].join(' · ')}
      >
        <StatChip label="Names to decide" value={namesToDecide(s)} />
      </span>
      <StatChip label="Open in 26AS" value={s.open.value_26as} />
      <StatChip label="Open in books" value={s.open.value_books} />
    </ChipStrip>
  )
}

function SortToggle<T extends string>({ value, onChange, options }: {
  value: T
  onChange: (v: T) => void
  options: { label: string; value: T }[]
}) {
  return (
    <span className="inline-flex shrink-0 overflow-hidden rounded-md border border-border text-xs">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`px-2 py-0.5 ${value === o.value ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </span>
  )
}

/** The "Split them" menu — name stands alone, per side. A dropdown built like
 *  InfoTip: click-toggled, closed on outside click. */
function SplitMenu({ names26, namesBk, onSplit }: {
  names26: string[]
  namesBk: string[]
  onSplit: (name: string, side: '26as' | 'books') => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])
  return (
    <span className="relative inline-flex" ref={ref}>
      <Btn size="xs" kind="subtle" onClick={() => setOpen((s) => !s)}>Split them</Btn>
      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-72 rounded-md border border-border bg-background p-1.5 shadow-lg">
          <p className="px-1.5 py-1 text-[10px] font-semibold uppercase text-muted-foreground">
            Name stands alone — nothing may weld it here again
          </p>
          {names26.map((n) => (
            <button key={`26:${n}`} type="button"
              className="block w-full rounded px-1.5 py-1 text-left text-xs hover:bg-muted"
              onClick={() => { setOpen(false); onSplit(n, '26as') }}>
              {n} <span className="text-muted-foreground">(26AS)</span>
            </button>
          ))}
          {namesBk.map((n) => (
            <button key={`bk:${n}`} type="button"
              className="block w-full rounded px-1.5 py-1 text-left text-xs hover:bg-muted"
              onClick={() => { setOpen(false); onSplit(n, 'books') }}>
              {n} <span className="text-muted-foreground">(books)</span>
            </button>
          ))}
        </div>
      )}
    </span>
  )
}

/** One category of the Excel's Party Reconciliation sheet, opened.
 *
 *  Loads only when the reader opens it (`useParties(..., enabled)`): the five
 *  sections together are every party in the run, and fetching all of them to
 *  render four collapsed headings would cost the page its load time for
 *  nothing.
 */
function CategoryPanel({
  runId,
  clientId,
  category,
  welded,
  weldedRows,
  onSplit,
  onConfirm,
  busy,
  suggested = [],
  strongGated = [],
}: {
  runId: string
  clientId?: string
  category: string
  /** this section's own recommendations, already filtered by `suggestionsFor` */
  suggested?: NameMappingRow[]
  /** strong pairs the Suggested gate killed, already filtered by
   *  `strongCandidates` — shown with their reasons, never as certainties */
  strongGated?: NameMappingRow[]
  /** party name -> weld score, for the parties the ENGINE joined and no
   *  approved alias yet covers. */
  welded: Map<string, number | null>
  /** the same parties, with the names the engine welded — the split
   *  menu needs the names, the sort needs only the score */
  weldedRows?: Map<string, { names_26as: string[]; names_books: string[] }>
  onSplit?: (name: string, side: '26as' | 'books') => void
  onConfirm: (party: string) => void
  busy: boolean
}) {
  // A one-sided category has no counterpart to tabulate — every books column
  // would be blank — so its panel is the matching surface instead of a table.
  const workbench = workbenchState(category, null, []) != null
  // A-Z is the default — twin spellings sit together
  const [sort, setSort] = useState<PartySortMode>('name')
  const q = useParties(runId, { category, page: 1, size: 200 }, !workbench)
  if (workbench) {
    // The machine's recommendations for THIS section, above the columns that
    // work the same names. `SuggestedPairs` renders nothing when there are
    // none — see `suggestionsFor`.
    return (
      <div className="space-y-2">
        <SuggestedPairs rows={suggested} runId={runId} clientId={clientId} />
        <StrongCandidates rows={strongGated} runId={runId} clientId={clientId} />
        <MatchWorkbench runId={runId} clientId={clientId} category={category} />
      </div>
    )
  }
  if (q.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>
  if (q.isError) return <p className="text-sm text-red-700">Could not load: {String(q.error)}</p>
  const { items, total } = q.data
  const mine = items.filter((p) => welded.has(p.party))
  const rows = partySort(items, sort, welded)
  return (
    <div className="space-y-1.5">
      <div className="flex flex-nowrap items-center justify-between gap-2">
        {/* the category's full sentence lives here now that the panel has no
            heading line of its own — same words, on a row that already exists */}
        <p className="text-xs text-muted-foreground">
          {category}
          {total > items.length
            && ` — showing the ${items.length} largest differences of ${total}`}
        </p>
        {/* Replaces the standalone "Joined by the engine" section. Those
            parties were always in this table — the only thing the section
            added was this ordering. Not decoration: the engine welds on
            `startswith`, not on a ratio, so size-of-gap order buries the
            least certain join. */}
        <div className="flex flex-nowrap items-center gap-1.5">
          {mine.length > 0 && (
            <>
              <span className="text-xs text-muted-foreground">{mine.length} joined by the engine</span>
              <InfoTip w={380}>
                The engine already joined these names for <b>this run&apos;s</b>{' '}
                figures — the row is live in the reconciliation now.{' '}
                <b>Keep together</b> writes a durable rule so future runs join
                them without guessing; <b>Split them</b> separates the names on
                the next run. They sort to the top until you decide.
              </InfoTip>
            </>
          )}
          <SortToggle
            value={sort}
            onChange={setSort}
            options={[
              { label: 'A–Z', value: 'name' },
              { label: 'Biggest gap', value: 'difference' },
              ...(mine.length > 0
                ? [{ label: 'Least certain join', value: 'confidence' as PartySortMode }]
                : []),
            ]}
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px] text-xs">
          <thead>
            <tr>
              <th className={th} style={{ width: '28%' }}>26AS names</th>
              <th className={th} style={{ width: '28%' }}>Books names</th>
              <th className={`${th} text-right`}>26AS TDS</th>
              <th className={`${th} text-right`}>Books TDS</th>
              <th className={`${th} text-right`}>Difference</th>
              <th className={th}>Joined by</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-t border-border align-top">
                <td className={`${td} break-words`}>
                  {(p.names_26as ?? []).map((n) => <p key={n} className="text-xs">{n}</p>)}
                </td>
                <td className={`${td} break-words`}>
                  {(p.ledger_names ?? []).map((n) => <p key={n} className="text-xs">{n}</p>)}
                </td>
                <td className={`${td} text-right`}><Money v={p.tds_26as} className="text-xs" /></td>
                <td className={`${td} text-right`}><Money v={p.ledger_dr} className="text-xs" /></td>
                <td className={`${td} text-right`}><Money v={p.diff} bold className="text-xs" /></td>
                <td className={td}>
                  <span className="flex flex-nowrap items-center gap-1.5">
                    {/* a join still awaiting its human is ALREADY in this
                        run's figures — the badge says the state, the basis
                        detail lives in the hover title */}
                    {welded.has(p.party) ? (
                      <Chip tone="violet" title={basisLabel(p.match_basis, p.score) ?? undefined}>
                        joined this run — confirm to keep
                      </Chip>
                    ) : (
                      basisLabel(p.match_basis, p.score) && (
                        <Chip tone="violet">{basisLabel(p.match_basis, p.score)}</Chip>
                      )
                    )}
                    {welded.has(p.party) && (
                      <Btn size="xs" loading={busy} onClick={() => onConfirm(p.party)}
                        className="border-violet-300 text-violet-700">
                        Keep together
                      </Btn>
                    )}
                    {/* The other half of an audit. Confirming freezes the
                        weld; nothing could reject one. Measured live, the
                        WRONG weld scores 1.000 (a different company pulled in
                        by a prefix hit) while the lowest score is correct —
                        so this cannot be offered only on low-scoring rows. */}
                    {welded.has(p.party) && onSplit && weldedRows?.get(p.party) && (
                      <SplitMenu
                        names26={weldedRows.get(p.party)!.names_26as}
                        namesBk={weldedRows.get(p.party)!.names_books}
                        onSplit={onSplit}
                      />
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** The Excel's category picture, as zone tabs.
 *
 *  Only the selected zone renders, so the working area starts high whichever
 *  zone is open. The default is the first zone with names to DECIDE, because
 *  opening on the Agreed archive was half of "the action is almost hidden".
 */
export function CategorySections({
  summary,
  runId,
  clientId,
  engineJoined,
  suggested,
  candidates = [],
}: {
  summary: NameMappingPage['summary']
  runId: string
  clientId?: string
  /** the run's engine-welded parties, from the worklist. `build()` already
   *  skips any an approved alias covers, so this IS the confirmable set. */
  engineJoined: NameMappingRow[]
  /** the machine's recommendations, dealt out to the sections whose names
   *  they are about (`suggestionsFor`). */
  suggested: NameMappingRow[]
  /** the open rows, from which each section takes its gated strong pairs —
   *  the ones the Suggested gate killed, shown WITH the reason it did */
  candidates?: NameMappingRow[]
}) {
  const accept = useAcceptPairing(runId)
  const create = useCreateAliasGroups(clientId ?? '')
  const welded = new Map(engineJoined.map((r) => [r.title, r.score]))
  const weldedRows = new Map(engineJoined.map((r) => [r.title, {
    names_26as: r.names_26as, names_books: r.names_books,
  }]))
  const cats = categorySections(summary.categories)
  const [zone, setZone] = useState<string | null>(null)
  const active = zone ?? defaultZone(summary.categories) ?? cats[0]?.category ?? null

  // Declaring a name its own company is an ALIAS, one-sided and flagged, which
  // is what holds it out of the engine's auto-match pools next run. It does
  // not move this run's figures: the weld already happened here.
  const split = async (name: string, side: '26as' | 'books') => {
    try {
      await create.mutateAsync([standAlone(name, side)])
      showToast({
        color: 'green',
        title: 'Recorded as its own company',
        message: `${name} will not be welded to anything from the next run on. `
          + 'This run keeps the figures it was produced with.',
      })
    } catch (e) {
      showToast({
        color: 'red',
        title: 'Could not record that',
        message: e instanceof ApiError ? e.message : String(e),
      })
    }
  }
  const confirm = async (party: string) => {
    const r = engineJoined.find((x) => x.title === party)
    if (!r) return
    try {
      await accept.mutateAsync({ ...confirmJoin(r), label: r.title.slice(0, 60) })
      showToast({
        color: 'green',
        title: 'Mapping confirmed',
        message: 'The join is frozen as an alias and survives data drift. '
          + 'Next run reads it as a manual alias.',
      })
    } catch (e) {
      showToast({
        color: 'red',
        title: 'Could not confirm',
        message: e instanceof ApiError ? e.message : String(e),
      })
    }
  }

  return (
    <div>
      {/* full-width tab strip; the full category sentence rides as a tooltip
          instead of costing the panel its first line */}
      <div className="flex border-b border-border">
        {cats.map((c) => (
          <button
            key={c.category}
            type="button"
            title={c.category}
            onClick={() => setZone(c.category)}
            className={`flex flex-1 items-center justify-center gap-1.5 border-b-2 px-2 py-2 text-sm ${
              active === c.category
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Chip tone={c.color as Tone}>{c.short}</Chip> ({c.count})
          </button>
        ))}
      </div>
      {cats.filter((c) => c.category === active).map((c) => (
        <div key={c.category} className="pt-2">
          <CategoryPanel
            runId={runId}
            clientId={clientId}
            category={c.category}
            welded={welded}
            weldedRows={weldedRows}
            onSplit={(n, sd) => void split(n, sd)}
            onConfirm={(p) => void confirm(p)}
            busy={accept.isPending}
            suggested={suggestionsFor(suggested, c.category)}
            strongGated={strongCandidates(candidates, c.category)}
          />
        </div>
      ))}
    </div>
  )
}

/** Approved mappings a run refuses to apply until someone signs for their
 *  reach. Outside every zone on purpose: a 67-name merge awaiting a signature
 *  is an exception, and an exception in an archive is a silence. */
export function WithheldBanner({
  rows,
  busy,
  onSignReach,
}: {
  rows: NameMappingRow[]
  busy: boolean
  onSignReach: (groupId: string, names26: string[], namesBk: string[]) => void
}) {
  if (!rows.length) return null
  return (
    <div className="rounded-lg border border-orange-300 bg-orange-50 p-3">
      <p className="mb-1.5 text-sm font-semibold text-orange-900">
        {rows.length} approved mapping(s) not applied
      </p>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.key} className="flex flex-nowrap items-center gap-2">
            <p className="flex-1 break-words text-sm text-foreground">
              <b>{r.title}</b> reaches {r.unsigned_names.length} name(s) nobody signed for
            </p>
            <Btn size="xs" kind="warning" loading={busy}
              onClick={() => onSignReach(r.group_id!, r.names_26as, r.names_books)}>
              Approve reach
            </Btn>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Zone 1 — the pairs the machine actually stands behind.
 *
 *  Two equal columns, because the reconciler is comparing two names and the
 *  layout must show two names. Short by construction: strong evidence AND
 *  closes real money AND the crossed-guard.
 */
export function SuggestedPairs({
  rows,
  runId,
  clientId,
}: {
  rows: NameMappingRow[]
  runId: string | null
  clientId?: string
}) {
  const accept = useAcceptPairing(runId)
  const rejectPair = useRejectPair(clientId ?? '')
  const [done, setDone] = useState<Set<string>>(new Set())
  const left = rows.filter((r) => !done.has(r.key))
  if (!left.length) return null

  // The other answer a proposal invites. Durable, so the pair does not come
  // back on the next visit; the row leaves the moment the server records it.
  const decline = (r: NameMappingRow, n26: string, nbk: string) => {
    rejectPair.mutate({ name_26as: n26, name_books: nbk }, {
      onSuccess: () => setDone((p) => new Set(p).add(r.key)),
      onError: (e) => showToast({
        color: 'red', title: 'Could not record that',
        message: e instanceof ApiError ? e.message : String(e),
      }),
    })
  }

  const acceptRows = async (targets: NameMappingRow[]) => {
    let ok = 0
    const failed: string[] = []
    for (const r of targets) {
      try {
        await accept.mutateAsync({ ...pairingOf(r), label: r.title.slice(0, 60) })
        setDone((p) => new Set(p).add(r.key))
        ok += 1
      } catch (e) {
        failed.push(e instanceof ApiError ? e.message : String(e))
      }
    }
    showToast({
      color: failed.length ? 'yellow' : 'green',
      title: `${ok} mapping(s) created`,
      message: failed.length
        ? failed[0]
        : 'They apply from the next run. Runs already made keep the mapping they used.',
    })
  }

  return (
    <div className="space-y-1">
      <div className="flex flex-nowrap items-center gap-2">
        <p className="text-sm font-bold text-foreground">Suggested pairs</p>
        <p className="text-xs text-muted-foreground">
          {left.length} pair(s) the engine is confident about
        </p>
        {left.length > 1 && (
          <Btn size="xs" kind="primary" loading={accept.isPending}
            onClick={() => void acceptRows(left)}>
            Accept all {left.length}
          </Btn>
        )}
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full min-w-[760px] text-xs">
          <thead>
            <tr>
              <th className={th} style={{ width: '34%' }}>26AS deductor</th>
              <th className={th} style={{ width: '34%' }}>Books party</th>
              <th className={th}>Why</th>
              <th className={`${th} text-right`}>₹ closes</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {left.map((r) => {
              const n26 = r.side === '26as' ? r.title : r.proposal!.name
              const nbk = r.side === '26as' ? r.proposal!.name : r.title
              return (
                <tr key={r.key} className="border-t border-border">
                  <td className={`${td} break-words`}>{n26}</td>
                  <td className={`${td} break-words`}>{nbk}</td>
                  <td className={`${td} text-muted-foreground`}>
                    shares {r.proposal!.evidence.join(', ')}
                  </td>
                  <td className={`${td} text-right`}>
                    <Money v={r.proposal!.closes ?? '0'} bold />
                  </td>
                  <td className={td}>
                    <span className="flex flex-nowrap gap-1">
                      <Btn size="xs" kind="primary" loading={accept.isPending}
                        onClick={() => void acceptRows([r])}>
                        Accept
                      </Btn>
                      <Btn size="xs" kind="subtle" loading={rejectPair.isPending}
                        onClick={() => decline(r, n26, nbk)}>
                        Not the same
                      </Btn>
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Strong pairs the Suggested gate killed, with the reason it did.
 *
 *  Strong sits on top, as candidates carrying their objection, never as the
 *  machine's own recommendation. "Map anyway" is deliberately per-row and
 *  there is no accept-all: overriding a stated objection is a judgement made
 *  one pair at a time.
 */
export function StrongCandidates({
  rows,
  runId,
  clientId,
}: {
  rows: NameMappingRow[]
  runId: string | null
  clientId?: string
}) {
  const accept = useAcceptPairing(runId)
  const rejectPair = useRejectPair(clientId ?? '')
  const [done, setDone] = useState<Set<string>>(new Set())
  const left = rows.filter((r) => !done.has(r.key))
  if (!left.length) return null

  // "Map anyway"'s missing sibling: decline the pair, durably — it stops
  // being proposed and both names fall back to their next-best pairing.
  const decline = (r: NameMappingRow, n26: string, nbk: string) => {
    rejectPair.mutate({ name_26as: n26, name_books: nbk }, {
      onSuccess: () => setDone((p) => new Set(p).add(r.key)),
      onError: (e) => showToast({
        color: 'red', title: 'Could not record that',
        message: e instanceof ApiError ? e.message : String(e),
      }),
    })
  }

  const mapAnyway = async (r: NameMappingRow) => {
    try {
      await accept.mutateAsync({ ...pairingOf(r), label: r.title.slice(0, 60) })
      setDone((p) => new Set(p).add(r.key))
      showToast({
        color: 'green',
        title: 'Mapping created',
        message: 'It applies from the next run. Runs already made keep the '
          + 'mapping they used.',
      })
    } catch (e) {
      showToast({
        color: 'red', title: 'Could not create the mapping',
        message: e instanceof ApiError ? e.message : String(e),
      })
    }
  }

  return (
    <div className="space-y-1">
      <div className="flex flex-nowrap items-center gap-2">
        <p className="text-sm font-bold text-foreground">Strong candidates</p>
        <p className="text-xs text-muted-foreground">
          {left.length} strong pair(s) the engine did NOT suggest — each says why
        </p>
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full min-w-[760px] text-xs">
          <thead>
            <tr>
              <th className={th} style={{ width: '26%' }}>26AS deductor</th>
              <th className={th} style={{ width: '26%' }}>Books party</th>
              <th className={th}>Why it is not suggested</th>
              <th className={`${th} text-right`}>₹ closes</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {left.map((r) => {
              const n26 = r.side === '26as' ? r.title : r.proposal!.name
              const nbk = r.side === '26as' ? r.proposal!.name : r.title
              return (
                <tr key={r.key} className="border-t border-border">
                  <td className={`${td} break-words`}>{n26}</td>
                  <td className={`${td} break-words`}>{nbk}</td>
                  <td className={td} style={{ maxWidth: 260 }}>
                    {/* one line, full reason on hover — the objection stays
                        reachable without costing the row its height */}
                    <p className="truncate text-xs text-orange-700"
                      title={gateText(r.proposal!.gate)}>
                      {gateText(r.proposal!.gate)}
                    </p>
                  </td>
                  <td className={`${td} text-right`}>
                    <Money v={r.proposal!.closes ?? '0'} />
                  </td>
                  <td className={td}>
                    <span className="flex flex-nowrap gap-1">
                      <Btn size="xs" kind="warning" loading={accept.isPending}
                        onClick={() => void mapAnyway(r)}>
                        Map anyway
                      </Btn>
                      <Btn size="xs" kind="subtle" loading={rejectPair.isPending}
                        onClick={() => decline(r, n26, nbk)}>
                        Not the same
                      </Btn>
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function TwoColumnNames({ r }: { r: NameMappingRow }) {
  const [open, setOpen] = useState(false)
  const cell = (names: string[]) => {
    const shown = open ? names : names.slice(0, 2)
    return (
      <td className={`${td} break-words`} style={{ width: '42%' }}>
        {shown.map((n) => <p key={n} className="text-xs">{n}</p>)}
        {names.length > 2 && !open && (
          <button type="button" className="text-xs text-primary hover:underline"
            onClick={() => setOpen(true)}>
            +{names.length - 2} more
          </button>
        )}
      </td>
    )
  }
  return (
    <>
      {cell(r.names_26as)}
      {cell(r.names_books)}
    </>
  )
}

/** The alias rules themselves — in force, awaiting approval, withheld, retired.
 *
 *  A rule spans several names and often several parties, so it could never be
 *  one of the party categories, and sitting among them made it look like it
 *  was. They get their own tab.
 *
 *  Withheld groups appear here AND as the banner on the mapping tab. That is
 *  deliberate duplication, not an oversight: a withheld group makes the
 *  mapping figures wrong — names read as unmapped when an approved alias
 *  exists — so the page showing those figures has to say so. `onSignReach` is
 *  the same handler both places call.
 */
export function RulesTab({
  zones,
  busy,
  canRetire,
  onLifecycle,
  onDetail,
  onSignReach,
}: {
  zones: NameMappingZones
  busy: boolean
  canRetire: boolean
  onLifecycle: (groupId: string, verb: 'retire' | 'reinstate') => void
  onDetail: (groupId: string) => void
  onSignReach: (groupId: string, names26: string[], namesBk: string[]) => void
}) {
  const d = zones.decided
  const table = (rows: NameMappingRow[], extra: (r: NameMappingRow) => ReactNode) => (
    <table className="w-full text-xs">
      <thead>
        <tr>
          <th className={th} style={{ width: '34%' }}>26AS names</th>
          <th className={th} style={{ width: '34%' }}>Books names</th>
          <th className={th} />
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className="border-t border-border align-top">
            <TwoColumnNames r={r} />
            <td className={td}>{extra(r)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )

  if (!d.mapped.length && !d.retired.length && !zones.withheld.length) {
    return (
      <p className="py-4 text-sm text-muted-foreground">
        No alias rules yet. Build one from the matching lists on the Name mapping tab —
        it applies from the next run.
      </p>
    )
  }

  return (
    <div className="space-y-6">
      {zones.withheld.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-base font-semibold text-foreground">
            Not applied — waiting on a signature ({zones.withheld.length})
          </h3>
          <WithheldBanner rows={zones.withheld} busy={busy} onSignReach={onSignReach} />
        </div>
      )}

      {d.mapped.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-base font-semibold text-foreground">In force ({d.mapped.length})</h3>
          {table(d.mapped, (r) => (
            <span className="flex flex-nowrap items-center justify-end gap-1.5">
              <span className="flex-1 text-xs text-muted-foreground">
                {r.title}
                {r.state === 'awaiting_approval' ? ' · awaiting approval' : ''}
              </span>
              {r.group_id != null && (
                <Btn size="xs" kind="subtle" onClick={() => onDetail(r.group_id!)}>
                  details
                </Btn>
              )}
              {canRetire && r.group_id != null && (
                <Btn size="xs" kind="subtle" loading={busy}
                  onClick={() => onLifecycle(r.group_id!, 'retire')}>
                  retire
                </Btn>
              )}
            </span>
          ))}
        </div>
      )}

      {d.retired.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-base font-semibold text-foreground">Retired ({d.retired.length})</h3>
          <p className="text-xs text-muted-foreground">
            Kept because the runs that used them still mean what they meant.
          </p>
          {table(d.retired, (r) => (
            <span className="flex flex-nowrap items-center justify-end gap-1.5">
              <span className="flex-1 text-xs text-muted-foreground">{r.title}</span>
              {canRetire && r.group_id != null && (
                <Btn size="xs" kind="subtle" loading={busy}
                  onClick={() => onLifecycle(r.group_id!, 'reinstate')}
                  className="text-teal-700">
                  reinstate
                </Btn>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
